/**
 * Contact d'un point de retrait ou de livraison (`contact_name` /
 * `contact_phone`, note backend du 2026-08-05).
 *
 * Module PUR → testable sans mock.
 *
 * ## La règle de validation est STRICTE côté serveur
 * `contact_phone` n'accepte que des **chiffres, `+` et espaces**. Un tiret, un
 * point ou des parenthèses font rejeter toute la course — or ce sont
 * exactement les caractères qu'un clavier téléphone produit, et que les gens
 * copient depuis leurs contacts (« 97 00 00 00 (bureau) », « +229-97-00-00-00 »).
 * D'où ce nettoyage systématique avant envoi.
 */

/**
 * ⚠️ CHAMPS HORS CONTRAT EMBARQUÉ.
 * `openapi.json` (v2.1.0) ne déclare `contact_name`/`contact_phone` ni sur
 * `StopLocation` ni sur `DeliveryAddress` — ils sont arrivés avec la note
 * backend du 2026-08-05. TypeScript ne les rejette pas (ces schémas tolèrent
 * des propriétés supplémentaires), mais il ne les VÉRIFIE pas non plus : une
 * faute de frappe passerait à la compilation et se perdrait silencieusement à
 * l'envoi. À revalider dès la régénération du contrat.
 */

/** Longueurs maximales imposées par le backend. */
export const CONTACT_NAME_MAX  = 120;
export const CONTACT_PHONE_MAX = 20;

export type TripContact = {
  contact_name?:  string;
  contact_phone?: string;
};

/**
 * Nettoie un numéro pour qu'il passe la validation backend.
 *
 * Ne garde que chiffres, `+` et espaces, puis compacte les espaces multiples et
 * tronque à la longueur maximale. Retourne `undefined` si rien d'exploitable ne
 * subsiste — un champ vide vaut mieux qu'un `""` qui occuperait le contact.
 */
export function sanitizeContactPhone(raw: string | undefined | null): string | undefined {
  if (!raw) return undefined;

  const kept = raw
    .replace(/[^\d+\s]/g, "")   // tirets, points, parenthèses… éliminés
    .replace(/\s+/g, " ")       // espaces multiples compactés
    .trim()
    .slice(0, CONTACT_PHONE_MAX);

  // Un « + » seul, ou une chaîne d'espaces, n'est pas un numéro.
  return /\d/.test(kept) ? kept : undefined;
}

/** Nettoie un nom : espaces compactés et troncature à la limite backend. */
export function sanitizeContactName(raw: string | undefined | null): string | undefined {
  if (!raw) return undefined;
  const kept = raw.replace(/\s+/g, " ").trim().slice(0, CONTACT_NAME_MAX);
  return kept.length > 0 ? kept : undefined;
}

/**
 * Construit le fragment `contact_*` à fusionner dans une adresse du payload.
 *
 * Les clés absentes sont OMISES plutôt qu'envoyées à `undefined` : le backend
 * traite l'absence comme « pas de contact », et la règle d'héritage s'applique
 * alors (l'article reprend le contact de la course).
 */
export function buildContactFields(contact: {
  name?:  string | null;
  phone?: string | null;
}): TripContact {
  const name  = sanitizeContactName(contact.name);
  const phone = sanitizeContactPhone(contact.phone);

  return {
    ...(name  ? { contact_name:  name }  : {}),
    ...(phone ? { contact_phone: phone } : {}),
  };
}

/**
 * Le numéro saisi est-il valide une fois nettoyé ?
 *
 * Le contact est OBLIGATOIRE depuis le 2026-08-05 : cette fonction alimente la
 * validation du formulaire, qui bloque l'envoi tant qu'un numéro exploitable
 * n'est pas saisi. On juge sur la version NETTOYÉE — « 97.00.00.00 » est
 * parfaitement valide une fois les séparateurs retirés.
 */
export function isUsableContactPhone(raw: string | undefined | null): boolean {
  const clean = sanitizeContactPhone(raw);
  // Au moins 8 chiffres : longueur d'un numéro national béninois.
  return clean != null && (clean.match(/\d/g)?.length ?? 0) >= 8;
}
